import React from 'react';

import { styled } from '@compiled/react';

import { SmartCardProvider } from '@atlaskit/link-provider';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/compiled';
import { ReactRenderer } from '@atlaskit/renderer';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';

import { guidelineMaxLength, nameMaxLength } from '../../constants';
import { type GuidelineRepresentation } from '../../types';
import { safeParseGuidelineADF } from '../../utils/safeParseGuidelineADF';
import { selectGuideline } from '../../utils/selectGuideline';

type ClassificationViewProps = {
	guideline?: string;
	guidelineADF?: string;
	guidelineRepresentation?: GuidelineRepresentation;
	name?: string;
};

// Maximum height for guideline content to enable scrolling for long text
const GUIDELINE_MAX_HEIGHT = '300px';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Name = styled.h2({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.heading.medium', fontFallback.heading.medium),
	color: token('color.text', '#172B4D'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Guideline = styled.p({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body', fontFallback.body.medium),
	color: token('color.text', '#172B4D'),
	whiteSpace: 'pre-wrap',
	overflowWrap: 'break-word',
	maxHeight: GUIDELINE_MAX_HEIGHT,
	overflowY: 'auto',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GuidelineADFContainer = styled.div({
	maxHeight: GUIDELINE_MAX_HEIGHT,
	overflowY: 'auto',
});

export const ClassificationView = ({
	name,
	guideline,
	guidelineADF,
	guidelineRepresentation = 'PLAIN',
}: ClassificationViewProps): React.JSX.Element => {
	const nameTruncated = (name ?? '').substring(0, nameMaxLength);

	// Select the appropriate guideline based on guidelineRepresentation
	// Prefers guidelineADF when in EDITOR mode, falls back to guideline
	const selectedGuideline = selectGuideline({
		guidelineADF,
		guideline,
		guidelineRepresentation,
	});

	// Parse ADF if in EDITOR mode - parse before truncation to avoid breaking JSON
	const parsedADF =
		guidelineRepresentation === 'EDITOR' ? safeParseGuidelineADF(selectedGuideline) : undefined;

	// Only truncate for plain text mode - ADF content shows full text with scrolling
	const guidelineTruncated = (selectedGuideline ?? '').substring(0, guidelineMaxLength);

	return (
		<Box>
			{nameTruncated && <Name id="data-classification-read-content-name">{nameTruncated}</Name>}
			{selectedGuideline && (
				<Box paddingBlockStart="space.200">
					{guidelineRepresentation === 'PLAIN' || !parsedADF ? (
						<Guideline id="data-classification-read-content-guideline">
							{guidelineTruncated}
						</Guideline>
					) : (
						<GuidelineADFContainer>
							<SmartCardProvider>
								<ReactRenderer document={parsedADF} />
							</SmartCardProvider>
						</GuidelineADFContainer>
					)}
				</Box>
			)}
		</Box>
	);
};
